"use client"

import { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import axios from "axios"
import { API_URL } from "../../config"
import "./CalculationsManagement.css"

const CalculationsManagement = () => {
  const [calculations, setCalculations] = useState([])
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Pagination and filtering state
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCalculations, setTotalCalculations] = useState(0)
  const [limit, setLimit] = useState(10)

  // Filter state
  const [filters, setFilters] = useState({
    search: "",
    user: "",
    dateFrom: "",
    dateTo: "",
    model: "",
    sort: "createdAt",
    order: "desc",
  })

  // Debounced search
  const [searchTerm, setSearchTerm] = useState("")

  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search: searchTerm }))
    }, 500)

    return () => clearTimeout(timer)
  }, [searchTerm])

  useEffect(() => {
    fetchCalculations()
  }, [currentPage, limit, filters])

  useEffect(() => {
    fetchUsers()
  }, [])

  const fetchCalculations = async () => {
    try {
      setLoading(true)
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: limit.toString(),
        ...filters,
      })

      const response = await axios.get(`${API_URL}/api/admin/calculations?${params}`)
      //   console.log("sohel" , response.data)
      setCalculations(response.data.calculations || [])
      setTotalCalculations(response.data.total || 0)
      setTotalPages(response.data.totalPages || 1)
      setLoading(false)
    } catch (err) {
      setError("Failed to fetch calculations")
      setLoading(false)
      console.error(err)

    }
  }

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/users?limit=1000`)
      setUsers(Array.isArray(response.data) ? response.data : [])
    } catch (err) {
      console.error("Failed to fetch users:", err)
    }
  }

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }))
    setCurrentPage(1) // Reset to first page when filtering
  }

  const handleSort = (sortField) => {
    const newOrder = filters.sort === sortField && filters.order === "asc" ? "desc" : "asc"
    setFilters((prev) => ({ ...prev, sort: sortField, order: newOrder }))
  }

  const clearFilters = () => {
    setFilters({
      search: "",
      user: "",
      dateFrom: "",
      dateTo: "",
      model: "",
      sort: "createdAt",
      order: "desc",
    })
    setSearchTerm("")
    setCurrentPage(1)
  }

  const exportCalculations = async () => {
    try {
      const params = new URLSearchParams({
        ...filters,
        limit: "10000", // Export all matching records
        export: "true",
      })

      const response = await axios.get(`${API_URL}/api/admin/calculations/export?${params}`, {
        responseType: "blob", // Important for binary file
      })

      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }))
      const link = document.createElement("a")
      link.href = url
      link.setAttribute("download", `calculations_${new Date().toISOString().split("T")[0]}.xlsx`)
      document.body.appendChild(link)
      link.click()
      link.remove()
    } catch (err) {
      console.error("Export failed:", err)
    }
  }


  const getSortIcon = (field) => {
    if (filters.sort !== field) return "↕️"
    return filters.order === "asc" ? "↑" : "↓"
  }

  if (loading && calculations.length === 0) {
    return (
      <div className="calculations-loading">
        <div className="spinner"></div>
        <p>Loading calculations...</p>
      </div>
    )
  }

  return (
    <div className="calculations-management">
      <div className="calculations-header">
        <div className="header-content">
          <h1>Calculations Management</h1>
          <p className="subtitle">Manage and monitor all WATref calculations</p>
        </div>
        <div className="header-actions">
          <button onClick={exportCalculations} className="export-btn">
            <i className="fas fa-download"></i>
            Export
          </button>
          <Link to="/admin" className="back-btn">
            <i className="fas fa-arrow-left"></i>
            Back to Dashboard
          </Link>
        </div>
      </div>

      {/* Filters Section */}
      <div className="filters-section">
        <div className="filters-row">
          <div className="filter-group">
            <label>Search</label>
            <input
              type="text"
              placeholder="Search by user name, email, or location..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="filter-group">
            <label>User</label>
            <select
              value={filters.user}
              onChange={(e) => handleFilterChange("user", e.target.value)}
              className="filter-select"
            >
              <option value="">All Users</option>
              {users.map((user) => (
                <option key={user._id} value={user._id}>
                  {user.name} ({user.email})
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Model</label>
            <input
              type="text"
              placeholder="Filter by model name..."
              value={filters.model}
              onChange={(e) => handleFilterChange("model", e.target.value)}
              className="filter-input"
            />
          </div>
        </div>

        <div className="filters-row">
          <div className="filter-group">
            <label>Date From</label>
            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => handleFilterChange("dateFrom", e.target.value)}
              className="filter-input"
            />
          </div>

          <div className="filter-group">
            <label>Date To</label>
            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => handleFilterChange("dateTo", e.target.value)}
              className="filter-input"
            />
          </div>

          <div className="filter-group">
            <label>Items per page</label>
            <select value={limit} onChange={(e) => setLimit(Number(e.target.value))} className="filter-select">
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>

          <div className="filter-actions">
            <button onClick={clearFilters} className="clear-filters-btn">
              <i className="fas fa-times"></i>
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Results Summary */}
      <div className="results-summary">
        <p>
          Showing {calculations.length} of {totalCalculations} calculations
          {filters.search && ` matching "${filters.search}"`}
        </p>
      </div>

      {/* Calculations Table */}
      <div className="calculations-table-container">
        <table className="calculations-table">
          <thead>
            <tr>
              <th onClick={() => handleSort("user.name")} className="sortable">
                User {getSortIcon("user.name")}
              </th>
              <th onClick={() => handleSort("date")} className="sortable">
                Calculation Date {getSortIcon("date")}
              </th>
              <th onClick={() => handleSort("district.name")} className="sortable">
                Location {getSortIcon("district.name")}
              </th>
              <th>Models</th>
              <th>Weather Data</th>
              <th onClick={() => handleSort("createdAt")} className="sortable">
                Created {getSortIcon("createdAt")}
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {calculations.length > 0 ? (
              calculations.map((calc) => (
                <tr key={calc._id}>
                  <td>
                    <div className="user-info">
                      <div className="user-name">{calc.user?.name || "Guest"}</div>
                      <div className="user-email">{calc.user?.email || "N/A"}</div>
                    </div>
                  </td>
                  <td>{new Date(calc.date).toLocaleDateString()}</td>
                  <td>
                    {calc.location?.district?.name && calc.location?.country?.name
                      ? `${calc.location.district.name}, ${calc.location.country.name}`
                      : calc.location?.coordinates
                        ? `${calc.location.coordinates.lat.toFixed(2)}, ${calc.location.coordinates.lng.toFixed(2)}`
                        : "N/A"}
                  </td>
                  <td>
                    <div className="models-list">
                      {calc.models?.map((model, index) => (
                        <span key={index} className="model-tag">
                          {model.name}: {model.value?.toFixed(2)}
                        </span>
                      )) || "No models"}
                    </div>
                  </td>
                  <td>
                    <div className="weather-info">
                      {calc.weatherData ? (
                        <>
                          <div>Temp: {calc.weatherData.temperature}°C</div>
                          <div>Humidity: {calc.weatherData.humidity}%</div>
                        </>
                      ) : (
                        "N/A"
                      )}
                    </div>
                  </td>
                  <td>{new Date(calc.createdAt).toLocaleDateString()}</td>
                  <td>
                    <div className="table-actions">
                      <button className="action-btn view-btn" title="View Details">
                        <i className="fas fa-eye"></i>
                      </button>
                      <button className="action-btn download-btn" title="Download Report">
                        <i className="fas fa-download"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="no-data">
                  {loading ? "Loading..." : "No calculations found"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="pagination-btn"
          >
            <i className="fas fa-chevron-left"></i>
            Previous
          </button>

          <div className="pagination-info">
            <span>
              Page {currentPage} of {totalPages}
            </span>
          </div>

          <button
            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="pagination-btn"
          >
            Next
            <i className="fas fa-chevron-right"></i>
          </button>
        </div>
      )}

      {error && (
        <div className="error-message">
          <i className="fas fa-exclamation-triangle"></i>
          {error}
        </div>
      )}
    </div>
  )
}

export default CalculationsManagement
